import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { getIsMobile } from '../../../../../utils/getIsMobile';
import { useExperimentFlag } from '../../../../../utils/useExperimentFlag';
import { useIsContentPromotionEnabled } from '../../../../../utils/useIsContentPromotionEnabled';
import { useTranslation } from '../../../../../utils/useTranslation';
import { Button } from '../../../../input/Button';
import { DataTestId } from '../../../../layout/DataTestIdWrapper';
import { Flex } from '../../../../layout/Flex';
import {
    ADDITIONAL_FIELDS_OPTIONS,
    BORDER_RADIUS,
    PROMOTION_OPTION,
    SOURCE_OPTIONS,
    STATUSES_OPTIONS,
} from '../../constants';
import { FeedManagementDateDropdown } from '../FeedManagementDateDropdown/FeedManagementDateDropdown';
import { FeedManagementDropdown } from '../FeedManagementDropdown/FeedManagementDropdown';
import { FeedManagementFiltersDrawer } from '../FeedManagementFiltersDrawer/FeedManagementFiltersDrawer';
import { FeedManagementMenu } from '../FeedManagementMenu/FeedManagementMenu';
import { FeedManagementFiltersProps } from './types';
import { getFiltersButtonText } from './utils';

export const FeedManagementFilters: React.FC<FeedManagementFiltersProps> = ({
    sourceValue,
    onChangeSource,
    statusValue,
    onChangeStatus,
    periodValue,
    dateFrom,
    dateTo,
    onChangeDate,
    menuValues,
    onChangeMenu,
    onChangeFilters,
    onExportClick,
}) => {
    const { isMobile } = getIsMobile();
    const contentPromotionEnabled = useIsContentPromotionEnabled();
    const t = useTranslation();
    const [visibleFiltersPopup, setVisibleFiltersPopup] = useState(false);
    const onOpen = useCallback(() => setVisibleFiltersPopup(true), []);
    const onClose = useCallback(() => setVisibleFiltersPopup(false), []);

    const enableExportStat = useExperimentFlag('export_statistic_xlsx');

    return (
        <Flex
            direction="row"
            width="100%"
            pl={{ xs: 2, sm: 3 }}
            pr={{ xs: 1, sm: 3 }}
            alignItems="center"
            spacing={{ xs: 1.5, sm: 2 }}
            borderRadius={BORDER_RADIUS}
            py={1.5}
            zIndex={2}
        >
            {isMobile ? (
                <>
                    <Button color="secondary" onClick={onOpen}>
                        {getFiltersButtonText(
                            sourceValue,
                            statusValue,
                            periodValue,
                            dateFrom,
                            dateTo,
                        )}
                    </Button>
                    {visibleFiltersPopup
                        ? createPortal(
                              <FeedManagementFiltersDrawer
                                  source={sourceValue}
                                  status={statusValue}
                                  period={periodValue}
                                  dateFrom={dateFrom}
                                  dateTo={dateTo}
                                  onChange={onChangeFilters}
                                  onClose={onClose}
                              />,
                              document.body,
                          )
                        : null}
                </>
            ) : (
                <>
                    <FeedManagementDropdown
                        value={sourceValue}
                        placeholder={t('Источник')}
                        options={SOURCE_OPTIONS}
                        onChange={onChangeSource}
                        dataTestId={DataTestId.FEED_MANAGEMENT_FILTER_SOURCE}
                    />
                    <FeedManagementDropdown
                        value={statusValue}
                        placeholder={t('Статус')}
                        options={STATUSES_OPTIONS}
                        onChange={onChangeStatus}
                        dataTestId={DataTestId.FEED_MANAGEMENT_FILTER_STATUS}
                    />
                    <FeedManagementDateDropdown
                        value={periodValue}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onChange={onChangeDate}
                        dataTestId={DataTestId.FEED_MANAGEMENT_FILTER_PERIOD}
                    />
                </>
            )}
            {enableExportStat ? (
                <Button color="secondary" ml="auto" onClick={onExportClick}>
                    {t('Скачать xlsx')}
                </Button>
            ) : null}
            <FeedManagementMenu
                dataTestId={DataTestId.FEED_MANAGEMENT_COLUMNS_MENU}
                ml={enableExportStat ? undefined : 'auto'}
                options={
                    contentPromotionEnabled
                        ? [...ADDITIONAL_FIELDS_OPTIONS].concat(PROMOTION_OPTION)
                        : ADDITIONAL_FIELDS_OPTIONS
                }
                values={menuValues}
                onChange={onChangeMenu}
            />
        </Flex>
    );
};
